/**
 * Supabase Auth Service for ConnectX
 *
 * Replaces the legacy Express/FastAPI auth endpoints with
 * direct Supabase Auth SDK calls.
 */
import { supabase } from '../lib/supabaseClient';
import type { Profile } from '../types/database.types';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

import { getAcquisitionParams } from '../utils/utmTracker';

const SESSION_LOCK_STOLEN_MESSAGE = 'was released because another request stole it';

function isSessionLockContentionError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message.toLowerCase().includes(SESSION_LOCK_STOLEN_MESSAGE)
  );
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

// ─── Sign Up ──────────────────────────────────────────────
export async function signUp(
  email: string,
  password: string,
  fullName: string,
  role: string = 'job_seeker',
  consentData?: { consentedAt: string; consentVersion: string; isAdult: boolean }
) {
  const acqParams = getAcquisitionParams();

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
        role,
        utm_source: acqParams.utm_source ?? null,
        utm_medium: acqParams.utm_medium ?? null,
        utm_campaign: acqParams.utm_campaign ?? null,
        ref_code: acqParams.ref_code ?? null,
      },
    },
  });

  if (error) throw error;

  if (data.user) {
    const updates: any = {};
    if (role !== 'job_seeker') {
      updates.role = role;
    }
    if (acqParams.utm_source) updates.utm_source = acqParams.utm_source;
    if (acqParams.utm_medium) updates.utm_medium = acqParams.utm_medium;
    if (acqParams.utm_campaign) updates.utm_campaign = acqParams.utm_campaign;
    if (acqParams.ref_code) updates.ref_code = acqParams.ref_code;

    if (consentData) {
      updates.consented_at = consentData.consentedAt;
      updates.consent_version = consentData.consentVersion;
      updates.is_adult = consentData.isAdult;
    }

    if (Object.keys(updates).length > 0) {
      await supabase
        .from('profiles')
        .update(updates)
        .eq('id', data.user.id);
    }
  }

  return data;
}

// ─── Sign In (email + password) ──────────────────────────
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;

  // Best-effort: do not fail sign-in if profile row or RLS blocks last_login update
  if (data.user) {
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ last_login: new Date().toISOString() } as any)
      .eq('id', data.user.id);
    if (profileError) {
      console.warn('signIn: could not update profiles.last_login', profileError);
    }
  }

  return data;
}

// ─── Sign Out ─────────────────────────────────────────────
export async function signOut() {
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// ─── Google OAuth ─────────────────────────────────────────

/** Stored before redirect so the callback page knows login vs sign-up flow. */
export const GOOGLE_OAUTH_INTENT_STORAGE_KEY = 'reffconnect_google_oauth_intent';

export type GoogleOAuthIntent = 'login' | 'signup';
export type OAuthIntent = GoogleOAuthIntent;

/** Read intent without removing (safe under React StrictMode double-mount). */
export function peekGoogleOAuthIntent(): OAuthIntent {
  try {
    return sessionStorage.getItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY) === 'login'
      ? 'login'
      : 'signup';
  } catch {
    return 'signup';
  }
}

/** Call once the OAuth callback has finished routing the user (success or failure). */
export function clearGoogleOAuthIntentStorage(): void {
  try {
    sessionStorage.removeItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const OAUTH_CALLBACK_PATH = '/oauth/google/success';

async function startOAuthRedirect(
  provider: 'google' | 'linkedin_oidc',
  intent: OAuthIntent
): Promise<{ url: string | null }> {
  try {
    sessionStorage.setItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY, intent);
  } catch {
    /* ignore */
  }

  const redirectTo = `${window.location.origin}${OAUTH_CALLBACK_PATH}`;

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo,
      // `prompt=select_account` is Google-only; passing it to LinkedIn breaks OIDC.
      ...(provider === 'google'
        ? { queryParams: { prompt: 'select_account' } }
        : {}),
      skipBrowserRedirect: false,
    },
  });
  if (error) throw error;

  if (!data?.url) {
    throw new Error(
      provider === 'linkedin_oidc'
        ? 'LinkedIn sign-in is not configured. Enable LinkedIn (OIDC) in Supabase Authentication → Providers.'
        : 'Could not start Google sign-in. Check Supabase Google provider settings.'
    );
  }

  if (typeof window !== 'undefined') {
    window.location.assign(data.url);
  }

  return { url: data.url };
}

export async function signInWithGoogle(options?: { intent?: OAuthIntent }) {
  const intent: OAuthIntent = options?.intent ?? 'signup';
  return startOAuthRedirect('google', intent);
}

export async function signInWithLinkedIn(options?: { intent?: OAuthIntent }) {
  const intent: OAuthIntent = options?.intent ?? 'signup';
  return startOAuthRedirect('linkedin_oidc', intent);
}

// ─── Password Reset ──────────────────────────────────────
export async function resetPasswordRequest(email: string) {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim();
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim();

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase configuration missing in client');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/request-password-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': supabaseAnonKey,
      'Authorization': `Bearer ${supabaseAnonKey}`
    },
    body: JSON.stringify({ 
      email,
      redirectTo: `${window.location.origin}/forgot-password?step=reset`,
    }),
  });

  if (!response.ok) {
    let errMsg = 'Failed to send recovery email';
    try {
      const json = await response.json();
      if (json && json.error) {
        errMsg = json.error;
      }
    } catch {}
    throw new Error(errMsg);
  }

  return await response.json();
}

export async function verifyRecoveryOtp(email: string, token: string) {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'recovery',
  });
  if (error) throw error;
  return data;
}

export async function exchangeCodeForSession(code: string) {
  const { data, error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) throw error;
  return data;
}

export async function updatePassword(newPassword: string) {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return data;
}

// ─── Session helpers ─────────────────────────────────────
export async function getSession() {
  const maxAttempts = 3;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      if (isSessionLockContentionError(error)) {
        if (attempt < maxAttempts) {
          await delay(attempt * 50);
          continue;
        }
        return null;
      }
      throw error;
    }
  }

  return null;
}

export async function getUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
}

// ─── Profile helpers ─────────────────────────────────────
export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId);
  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }
  return data && data.length > 0 ? data[0] : null;
}

/**
 * Public profile lookup: safely fetches non-sensitive professional profile fields
 * and excludes private PII (primary_email, office_email, mobile_number, google_sub).
 */
export async function getPublicProfile(userId: string): Promise<Partial<Profile> | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, designation, company_name, location, role, is_active, is_verified, profile_picture, linkedin_url, github_link, portfolio_url, bio, skills, industry, languages, years_of_experience, work_modes, notice_period, availability_status, preferred_locations, education_entries, project_entries, certification_entries, work_history_entries, achievement_entries, hobbies, user_code, created_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.warn('getPublicProfile error:', error.message);
    return null;
  }
  return data || null;
}

/**
 * Fallback profile lookup by email.  Works when Google OAuth creates a
 * separate user ID from the original email/password account.
 *
 * Requires an RLS policy on `profiles` that allows:
 *   SELECT … WHERE auth.email() = primary_email
 */
export async function getProfileByEmail(email: string): Promise<Profile | null> {
  if (!email) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('primary_email', email.toLowerCase().trim());
  if (error) {
    console.warn('getProfileByEmail:', error.message);
    return null;
  }
  return data && data.length > 0 ? data[0] : null;
}

/**
 * `user_onboarding.user_id` often FK-references `profiles.id`. New OAuth users
 * can be signed in before a `profiles` row exists if the DB trigger is missing
 * or failed — then finishing onboarding hits a foreign key violation.
 */
export async function ensureProfileForAuthUser(user: User): Promise<void> {
  const existing = await getProfile(user.id);
  if (existing) return;

  const primaryEmail = (user.email ?? '').trim();
  if (!primaryEmail) {
    throw new Error(
      'Your account has no email on file, so we cannot create your profile. Contact support or try signing up with email.'
    );
  }

  const meta = user.user_metadata || {};
  
  // Check if this is an admin user to avoid creating a customer profile
  if (
    meta.role === 'admin' ||
    meta.role === 'super_admin' ||
    meta.is_admin === true ||
    meta.is_admin === 'true'
  ) {
    throw new Error(
      'This email is registered as an administrator and cannot be used to create a regular user profile.'
    );
  }

  try {
    const { data: isAdmin } = await supabase
      .from('admin_users')
      .select('primary_email')
      .eq('primary_email', primaryEmail);

    if (isAdmin && isAdmin.length > 0) {
      throw new Error(
        'This email is registered as an administrator and cannot be used to create a regular user profile.'
      );
    }
  } catch (err) {
    if (err instanceof Error && err.message.includes('administrator')) {
      throw err;
    }
    console.warn('ensureProfileForAuthUser: failed to check admin status', err);
  }

  const fullName =
    (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
    (typeof meta.name === 'string' && meta.name.trim()) ||
    primaryEmail.split('@')[0] ||
    'User';

  const isGoogle = user.identities?.some((i) => i.provider === 'google') ?? false;
  const isLinkedIn =
    user.identities?.some((i) => i.provider === 'linkedin_oidc' || i.provider === 'linkedin') ??
    false;

  const { error } = await supabase.from('profiles').upsert(
    {
      id: user.id,
      full_name: fullName,
      primary_email: primaryEmail,
      auth_provider: isGoogle ? 'google' : isLinkedIn ? 'linkedin' : 'local',
      role: 'job_seeker',
      is_active: true,
      is_verified: false,
      email_verified: Boolean(user.email_confirmed_at),
    } as any,
    { onConflict: 'id', ignoreDuplicates: true }
  );

  if (error) {
    if (error.code === '23505') return;
    throw error;
  }

  const after = await getProfile(user.id);
  if (!after) {
    throw new Error(
      'Could not create your profile (database policy may block inserts). Ask an admin to verify the profiles RLS policy and the new-user trigger.'
    );
  }
}

/** True when this user has finished app onboarding (saved in DB). Used to block Google-from-login for brand-new OAuth users, who still get a profiles row from triggers. */
export async function hasCompletedOnboarding(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_onboarding')
    .select('completed_at')
    .eq('user_id', userId);
  if (error) {
    console.error('hasCompletedOnboarding:', error);
    return false;
  }
  const record = data && data.length > 0 ? data[0] : null;
  return Boolean(record?.completed_at);
}

/** Dashboard path from profiles.role (set when onboarding is submitted). */
export function dashboardPathForRole(role: string | null | undefined): string {
  return role === 'referrer' ? '/referral-dashboard' : '/jobseeker-dashboard';
}

/** Resolve profile for OAuth user (by auth id, then email for linked accounts). */
export async function findProfileForAuthUser(user: User): Promise<{
  profile: Profile | null;
  onboardingUserId: string;
}> {
  let profile = await getProfile(user.id);
  let onboardingUserId = user.id;

  if (!profile && user.email) {
    const byEmail = await getProfileByEmail(user.email);
    if (byEmail) {
      profile = byEmail;
      onboardingUserId = byEmail.id;
    }
  }

  return { profile, onboardingUserId };
}

export type OAuthCallbackResult =
  | { action: 'reject'; message: string; redirectTo: '/login' | '/signup' }
  | { action: 'onboarding'; userData: { name: string; email: string } }
  | { action: 'dashboard'; path: string };

/**
 * Login OAuth: only users who finished signup/onboarding (user_onboarding.completed_at).
 * Signup OAuth: new users → onboarding; fully registered users → sign in instead.
 *
 * A user is "fully registered" only when onboarding is complete AND a profiles row exists.
 * Supabase always creates auth.users on OAuth; profiles may be empty if rows were deleted
 * or the DB trigger failed — those users should be allowed through sign-up again.
 */
export async function resolveOAuthCallback(
  session: Session,
  intent: OAuthIntent
): Promise<OAuthCallbackResult> {
  const user = session.user;
  const email = (user.email ?? '').trim();
  const name =
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    email.split('@')[0] ||
    'User';

  const { profile, onboardingUserId } = await findProfileForAuthUser(user);
  const onboardingComplete = await hasCompletedOnboarding(onboardingUserId);
  const resolvedProfile = profile ?? (await getProfile(user.id));
  const fullyRegistered = onboardingComplete && Boolean(resolvedProfile);

  if (intent === 'login') {
    if (!onboardingComplete) {
      return {
        action: 'reject',
        message:
          'No account found for this email. Please create an account on the Sign Up page first.',
        redirectTo: '/login',
      };
    }

    if (!resolvedProfile) {
      await ensureProfileForAuthUser(user);
    }
    const roleProfile = resolvedProfile ?? (await getProfile(user.id));
    return { action: 'dashboard', path: dashboardPathForRole(roleProfile?.role) };
  }

  // Sign up flow
  if (fullyRegistered) {
    return {
      action: 'reject',
      message: 'An account with this email already exists. Please sign in instead.',
      redirectTo: '/login',
    };
  }

  await ensureProfileForAuthUser(user);
  return { action: 'onboarding', userData: { name, email } };
}

export async function clearOAuthSession(): Promise<void> {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  await signOut();
}

export async function updateProfile(userId: string, updates: Partial<Profile>) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates as any)
    .eq('id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── Auth state listener ─────────────────────────────────
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
) {
  return supabase.auth.onAuthStateChange(callback);
}

// ─── Resend verification email ───────────────────────────
export async function resendVerificationEmail(email: string) {
  const { data, error } = await supabase.auth.resend({
    type: 'signup',
    email,
  });
  if (error) throw error;
  return data;
}

// Re-export types for convenience
export type { User, Session };
